if (!customElements.get('media-card-carousel')) {
  customElements.define('media-card-carousel', class extends customElements.get('media-video-player') {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._desktop = window.matchMedia('(min-width: 1000px)');
      this._viewport = this.querySelector('[data-carousel-viewport]');
      this._slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this._dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this._previous = this.querySelector('[data-carousel-prev]');
      this._next = this.querySelector('[data-carousel-next]');
      this._status = this.querySelector('[data-carousel-status]');
      this._index = 0;
      const options = { signal: this._controller.signal };

      if (!this._viewport || !this._slides.length) return;

      // Reuse the theme's Swiper library, without changing its shared component.
      if (typeof window.Swiper === 'function') {
        this._swiper = new window.Swiper(this._viewport, {
          slidesPerView: 'auto',
          slidesPerGroup: 1,
          spaceBetween: 15.3846,
          breakpoints: { 1000: { spaceBetween: 32 } },
          loop: false,
          autoplay: false,
          speed: this._motion.matches ? 0 : 400,
          grabCursor: this._slides.length > 1,
          watchOverflow: true,
          // Each card, including the last one, has its own left-aligned stop.
          slidesOffsetAfter: () => Math.max(0, this._viewport.clientWidth - this._slides.at(-1).offsetWidth),
          a11y: false,
          on: {
            slideChange: (swiper) => this._updateControls(swiper.activeIndex),
          },
        });
      } else {
        // Native scrolling and snap remain usable if Swiper cannot load.
        // Match Swiper's end spacing so the last card also has its own snap stop.
        this._endSpacer = document.createElement('div');
        this._endSpacer.setAttribute('aria-hidden', 'true');
        this._slides.at(-1).after(this._endSpacer);
        const updateEndSpacing = () => {
          const lastSlide = this._slides.at(-1);
          const space = Math.max(0, this._viewport.clientWidth - lastSlide.offsetWidth);
          const gap = parseFloat(getComputedStyle(lastSlide.parentElement).columnGap) || 0;
          this._endSpacer.hidden = space === 0;
          this._endSpacer.style.flex = `0 0 ${space}px`;
          // Cancel the extra flex gap introduced by the spacer itself.
          this._endSpacer.style.marginLeft = `${-gap}px`;
        };
        updateEndSpacing();
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(updateEndSpacing);
          this._resizeObserver.observe(this._viewport);
          this._resizeObserver.observe(this._slides.at(-1));
        } else {
          window.addEventListener('resize', updateEndSpacing, options);
        }
        this._viewport.addEventListener('scroll', () => {
          const left = this._viewport.scrollLeft;
          const index = this._slides.reduce((closest, slide, i) =>
            Math.abs(slide.offsetLeft - left) < Math.abs(this._slides[closest].offsetLeft - left) ? i : closest, 0);
          this._updateControls(index);
        }, { ...options, passive: true });
      }

      this.querySelector('[data-carousel-controls]')?.removeAttribute('hidden');
      this._previous?.addEventListener('click', () => this._goTo(this._index - 1), options);
      this._next?.addEventListener('click', () => this._goTo(this._index + 1), options);
      this._dots.forEach((button, index) => button.addEventListener('click', () => this._goTo(index), options));
      this._viewport.addEventListener('keydown', (event) => {
        if (event.target !== this._viewport) return;
        const targets = { ArrowLeft: this._index - 1, ArrowRight: this._index + 1, Home: 0, End: this._slides.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this._goTo(targets[event.key]);
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this._slides.indexOf(event.target.closest('[data-carousel-slide]'));
        if (index >= 0) this._goTo(index, 0);
      }, options);

      super.connectedCallback();
      if ('IntersectionObserver' in window) {
        this._slideObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { entry.target.inert = !entry.isIntersecting; });
        }, { root: this._viewport, threshold: 0 });
        this._slides.forEach((slide) => this._slideObserver.observe(slide));
      }
      this._motion.addEventListener('change', () => {
        if (this._swiper) this._swiper.params.speed = this._motion.matches ? 0 : 400;
      }, options);
      this._updateControls(0);
    }

    _goTo(index, speed) {
      index = Math.max(0, Math.min(index, this._slides.length - 1));
      if (this._swiper) {
        this._swiper.slideTo(index, speed ?? (this._motion.matches ? 0 : 400));
      } else {
        this._viewport.scrollTo({ left: this._slides[index].offsetLeft, behavior: this._motion.matches || speed === 0 ? 'auto' : 'smooth' });
      }
    }

    _updateControls(index) {
      this._index = index;
      if (this._previous) this._previous.disabled = index === 0;
      if (this._next) this._next.disabled = index === this._slides.length - 1;
      this._dots.forEach((button, i) => {
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (this._status) this._status.textContent = `${index + 1} / ${this._slides.length}`;
    }

    disconnectedCallback() {
      this._controller?.abort();
      super.disconnectedCallback();
      this._slideObserver?.disconnect();
      this._resizeObserver?.disconnect();
      this._endSpacer?.remove();
      this._slides?.forEach((slide) => { slide.inert = false; });
      this._swiper?.destroy(true, true);
      this._controller = null;
      this._swiper = null;
    }
  });
}
